import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  Input,
  Menu,
  Modal,
  ModalDialog,
  Option,
  Select,
  Sheet,
  Stack,
  Switch,
  Table,
  Tooltip,
  Typography,
} from "@mui/joy";
import FlagIcon from "@mui/icons-material/Flag";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Loading from "../components/Loading";
import {
  useDeleteMetalMindAdminFeatureFlag,
  useListMetalMindAdminFeatureFlags,
  useSetMetalMindAdminFeatureFlag,
} from "../services/metalmindAdmin";
import { isKnownFeatureFlagKey } from "./metalmindAdmin/knownFeatureFlagKeys";
import {
  EnvFeatureFlag,
  EnvFeatureFlagWrite,
  FlagSource,
  ValueType,
} from "./metalmindAdmin/types";

interface MetalMindAdminFeatureFlagsViewProps {
  items: EnvFeatureFlag[];
  loading?: boolean;
  error?: string | null;
  onSave?: (flagKey: string, nextValue: EnvFeatureFlagWrite) => Promise<void>;
  onDelete?: (flagKey: string) => Promise<void>;
}

const sourceChipLabel: Record<FlagSource, string> = {
  database: "DB",
  environment: "env",
  default: "default",
  absent: "absent",
};

const sourceChipColor: Record<FlagSource, any> = {
  database: "primary",
  environment: "warning",
  default: "neutral",
  absent: "danger",
};

const valueTypeLabel: Record<ValueType, string> = {
  bool: "bool",
  int: "int",
  str: "str",
};

const formatValue = (value: boolean | number | string) => {
  if (typeof value === "boolean") return value ? "true" : "false";
  if (typeof value === "number") return String(value);
  return value || "''";
};

const coerceValueForType = (
  nextValue: boolean | number | string,
  nextType: ValueType,
) => {
  switch (nextType) {
    case "bool":
      return typeof nextValue === "boolean" ? nextValue : false;
    case "int":
      return typeof nextValue === "number"
        ? nextValue
        : Number.parseInt(String(nextValue || "0"), 10) || 0;
    case "str":
    default:
      return String(nextValue ?? "");
  }
};

const wrappingCellSx = {
  whiteSpace: "normal",
  overflowWrap: "anywhere",
  wordBreak: "break-word",
} as const;

const monospaceCellSx = {
  ...wrappingCellSx,
  fontFamily: "monospace",
} as const;

const truncatedTextSx = {
  display: "block",
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
} as const;

const metadataLabelSx = {
  color: "text.tertiary",
  textTransform: "uppercase",
  letterSpacing: "0.08em",
} as const;

const stringifyApiDetail = (detail: unknown): string | null => {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => stringifyApiDetail(item))
      .filter(Boolean);
    return messages.length > 0 ? messages.join(" ") : null;
  }
  if (detail && typeof detail === "object") {
    const record = detail as Record<string, unknown>;
    if (typeof record.msg === "string") return record.msg;
    if (typeof record.message === "string") return record.message;
    if (typeof record.error === "string") return record.error;
  }
  return null;
};

const getAdminErrorMessage = (err: unknown, fallback: string) => {
  const error = err as {
    message?: string;
    response?: {
      detail?: unknown;
      message?: unknown;
      error?: unknown;
    };
  };
  return (
    stringifyApiDetail(error?.response?.detail) ||
    stringifyApiDetail(error?.response?.message) ||
    stringifyApiDetail(error?.response?.error) ||
    error?.message ||
    fallback
  );
};

const MetadataRow: React.FC<{
  label: string;
  children: React.ReactNode;
}> = ({ label, children }) => (
  <Stack spacing={0.25}>
    <Typography level="body-xs" sx={metadataLabelSx}>
      {label}
    </Typography>
    <Box>{children}</Box>
  </Stack>
);

export const MetalMindAdminFeatureFlagsView: React.FC<
  MetalMindAdminFeatureFlagsViewProps
> = ({ items, loading = false, error = null, onSave, onDelete }) => {
  const [editingFlag, setEditingFlag] = useState<EnvFeatureFlag | null>(null);
  const [draftValueType, setDraftValueType] = useState<ValueType>("bool");
  const [draftValue, setDraftValue] = useState<boolean | number | string>(
    false,
  );
  const [draftDescription, setDraftDescription] = useState("");
  const [modalError, setModalError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deletingKey, setDeletingKey] = useState<string | null>(null);
  const [savingToggleKey, setSavingToggleKey] = useState<string | null>(null);
  const [expandedDescriptions, setExpandedDescriptions] = useState<Set<string>>(
    () => new Set(),
  );
  const [metadataAnchor, setMetadataAnchor] = useState<HTMLElement | null>(
    null,
  );
  const [metadataFlagKey, setMetadataFlagKey] = useState<string | null>(null);

  const sortedItems = useMemo(
    () =>
      [...items].sort((left, right) =>
        left.flag_key.localeCompare(right.flag_key),
      ),
    [items],
  );

  const metadataFlag = useMemo(
    () =>
      metadataFlagKey
        ? sortedItems.find((flag) => flag.flag_key === metadataFlagKey) || null
        : null,
    [metadataFlagKey, sortedItems],
  );

  const openEdit = (flag: EnvFeatureFlag) => {
    setEditingFlag(flag);
    setDraftValueType(flag.value_type);
    setDraftValue(flag.value);
    setDraftDescription(flag.description || "");
    setModalError(null);
    setActionError(null);
  };

  const closeEdit = () => {
    setEditingFlag(null);
    setDraftValueType("bool");
    setDraftValue(false);
    setDraftDescription("");
    setModalError(null);
  };

  const openMetadata = (
    event: React.MouseEvent<HTMLElement>,
    flag: EnvFeatureFlag,
  ) => {
    setMetadataAnchor(event.currentTarget);
    setMetadataFlagKey(flag.flag_key);
  };

  const closeMetadata = () => {
    setMetadataAnchor(null);
    setMetadataFlagKey(null);
  };

  const handleSave = async () => {
    if (!editingFlag || !onSave) return;

    if (draftValueType === "int" && Number.isNaN(Number(draftValue))) {
      setModalError("Enter a valid integer value.");
      return;
    }

    try {
      setSaving(true);
      setModalError(null);
      await onSave(editingFlag.flag_key, {
        value: draftValueType === "int" ? Number(draftValue) : draftValue,
        value_type: draftValueType,
        description: draftDescription.trim() || null,
      });
      setActionError(null);
      closeEdit();
    } catch (err: unknown) {
      setModalError(getAdminErrorMessage(err, "Failed to save feature flag."));
    } finally {
      setSaving(false);
    }
  };

  const handleBooleanToggle = async (
    flag: EnvFeatureFlag,
    nextChecked: boolean,
  ) => {
    if (!onSave || savingToggleKey) return;

    try {
      setSavingToggleKey(flag.flag_key);
      setActionError(null);
      await onSave(flag.flag_key, {
        value: nextChecked,
        value_type: "bool",
        description: flag.description || null,
      });
    } catch (err: unknown) {
      setActionError(getAdminErrorMessage(err, "Failed to save feature flag."));
    } finally {
      setSavingToggleKey(null);
    }
  };

  const handleDelete = async (flagKey: string) => {
    if (!onDelete) return;

    try {
      setDeletingKey(flagKey);
      await onDelete(flagKey);
      setActionError(null);
      closeMetadata();
    } catch (err: unknown) {
      setActionError(
        getAdminErrorMessage(err, "Failed to delete feature flag override."),
      );
    } finally {
      setDeletingKey(null);
    }
  };

  const toggleDescription = (flagKey: string) => {
    setExpandedDescriptions((previous) => {
      const next = new Set(previous);
      if (next.has(flagKey)) {
        next.delete(flagKey);
      } else {
        next.add(flagKey);
      }
      return next;
    });
  };

  const consumerWarning = (flagKey: string) => !isKnownFeatureFlagKey(flagKey);

  return (
    <Stack spacing={2}>
      {error && (
        <Alert color="danger" variant="soft">
          {error}
        </Alert>
      )}
      {actionError && (
        <Alert color="danger" variant="soft">
          {actionError}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center">
              <FlagIcon color="primary" />
              <Typography level="h3">Environment feature flags</Typography>
            </Stack>

            {loading ? (
              <Loading />
            ) : sortedItems.length === 0 ? (
              <Alert color="neutral" variant="soft">
                No environment feature flags are visible in this environment.
              </Alert>
            ) : (
              <Sheet
                variant="outlined"
                sx={{ borderRadius: "8px", overflowX: "hidden" }}
              >
                <Table
                  hoverRow
                  sx={{
                    width: "100%",
                    minWidth: 0,
                    tableLayout: "fixed",
                    "& th": {
                      whiteSpace: "nowrap",
                    },
                    "& td": {
                      verticalAlign: "middle",
                      minWidth: 0,
                    },
                  }}
                >
                  <thead>
                    <tr>
                      <th style={{ width: "30%" }}>Flag</th>
                      <th>Description</th>
                      <th style={{ width: 190 }}>Value</th>
                      <th style={{ width: 150, textAlign: "right" }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedItems.map((flag) => {
                      const descriptionExpanded = expandedDescriptions.has(
                        flag.flag_key,
                      );
                      const isBoolean = flag.value_type === "bool";
                      const isSavingToggle = savingToggleKey === flag.flag_key;

                      return (
                        <tr key={flag.flag_key}>
                          <td>
                            <Typography
                              component="code"
                              level="body-sm"
                              sx={{ ...monospaceCellSx, ...truncatedTextSx }}
                              title={flag.flag_key}
                            >
                              {flag.flag_key}
                            </Typography>
                          </td>
                          <td>
                            <Typography
                              level="body-sm"
                              role={flag.description ? "button" : undefined}
                              tabIndex={flag.description ? 0 : undefined}
                              title={
                                flag.description
                                  ? "Click to expand or collapse"
                                  : undefined
                              }
                              onClick={() =>
                                flag.description &&
                                toggleDescription(flag.flag_key)
                              }
                              onKeyDown={(event) => {
                                if (
                                  flag.description &&
                                  (event.key === "Enter" || event.key === " ")
                                ) {
                                  event.preventDefault();
                                  toggleDescription(flag.flag_key);
                                }
                              }}
                              sx={{
                                ...(descriptionExpanded
                                  ? wrappingCellSx
                                  : truncatedTextSx),
                                cursor: flag.description
                                  ? "pointer"
                                  : "default",
                              }}
                            >
                              {flag.description || "—"}
                            </Typography>
                          </td>
                          <td>
                            {isBoolean ? (
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                sx={{ minWidth: 0 }}
                              >
                                <Switch
                                  size="sm"
                                  checked={Boolean(flag.value)}
                                  disabled={!onSave || Boolean(savingToggleKey)}
                                  onChange={(event) =>
                                    void handleBooleanToggle(
                                      flag,
                                      event.target.checked,
                                    )
                                  }
                                  slotProps={{
                                    input: {
                                      "aria-label": `Toggle ${flag.flag_key}`,
                                    },
                                  }}
                                />
                                <Typography level="body-sm">
                                  {Boolean(flag.value) ? "On" : "Off"}
                                </Typography>
                                {isSavingToggle && (
                                  <Chip
                                    size="sm"
                                    variant="soft"
                                    color="primary"
                                  >
                                    Saving…
                                  </Chip>
                                )}
                              </Stack>
                            ) : (
                              <Typography
                                component="code"
                                level="body-sm"
                                sx={{
                                  ...monospaceCellSx,
                                  ...truncatedTextSx,
                                }}
                                title={formatValue(flag.value)}
                              >
                                {formatValue(flag.value)}
                              </Typography>
                            )}
                          </td>
                          <td>
                            <Stack
                              direction="row"
                              spacing={0.5}
                              justifyContent="flex-end"
                              alignItems="center"
                              sx={{ minWidth: 0 }}
                            >
                              {!isBoolean && (
                                <Button
                                  size="sm"
                                  variant="outlined"
                                  color="neutral"
                                  startDecorator={<EditOutlinedIcon />}
                                  onClick={() => openEdit(flag)}
                                >
                                  Edit
                                </Button>
                              )}
                              <Tooltip title="Flag details">
                                <IconButton
                                  size="sm"
                                  variant="plain"
                                  color="neutral"
                                  aria-label={`Details for ${flag.flag_key}`}
                                  onClick={(event) => openMetadata(event, flag)}
                                >
                                  <InfoOutlinedIcon />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </Sheet>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Menu
        anchorEl={metadataAnchor}
        open={Boolean(metadataAnchor)}
        onClose={closeMetadata}
        placement="bottom-end"
        sx={{ p: 1.25, minWidth: 320, maxWidth: 380 }}
      >
        {metadataFlag && (
          <Stack spacing={1.25} sx={{ minWidth: 0 }}>
            <MetadataRow label="Type">
              <Chip size="sm" variant="soft" color="neutral">
                {valueTypeLabel[metadataFlag.value_type]}
              </Chip>
            </MetadataRow>
            <MetadataRow label="Source">
              <Chip
                size="sm"
                variant="soft"
                color={sourceChipColor[metadataFlag.source]}
              >
                {sourceChipLabel[metadataFlag.source]}
              </Chip>
            </MetadataRow>
            <MetadataRow label="Consumer">
              {consumerWarning(metadataFlag.flag_key) ? (
                <Chip
                  size="sm"
                  variant="soft"
                  color="warning"
                  startDecorator={<WarningAmberIcon />}
                >
                  No consumer found
                </Chip>
              ) : (
                <Chip size="sm" variant="soft" color="success">
                  Known
                </Chip>
              )}
            </MetadataRow>
            <MetadataRow label="Set by">
              <Typography level="body-sm" sx={wrappingCellSx}>
                {metadataFlag.set_by_email || "—"}
              </Typography>
            </MetadataRow>
            <MetadataRow label="Set at">
              <Typography level="body-sm" sx={wrappingCellSx}>
                {metadataFlag.set_at || "—"}
              </Typography>
            </MetadataRow>
            <Divider />
            {metadataFlag.source === "database" ? (
              <Button
                size="sm"
                variant="plain"
                color="danger"
                startDecorator={<DeleteOutlineIcon />}
                loading={deletingKey === metadataFlag.flag_key}
                onClick={() => handleDelete(metadataFlag.flag_key)}
              >
                Delete DB override
              </Button>
            ) : (
              <Typography level="body-xs" color="neutral">
                Only DB overrides can be deleted.
              </Typography>
            )}
          </Stack>
        )}
      </Menu>

      <Modal open={Boolean(editingFlag)} onClose={closeEdit}>
        <ModalDialog minWidth={420}>
          <DialogTitle>Edit feature flag</DialogTitle>
          <DialogContent>{editingFlag?.flag_key}</DialogContent>

          <Stack spacing={1.5}>
            <FormControl>
              <FormLabel>Value type</FormLabel>
              <Select
                value={draftValueType}
                onChange={(_, nextValue) => {
                  if (!nextValue) return;
                  setDraftValueType(nextValue);
                  setDraftValue(coerceValueForType(draftValue, nextValue));
                }}
              >
                <Option value="bool">Boolean</Option>
                <Option value="int">Integer</Option>
                <Option value="str">String</Option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>Value</FormLabel>
              {draftValueType === "bool" ? (
                <>
                  <Switch
                    checked={Boolean(draftValue)}
                    onChange={(event) => setDraftValue(event.target.checked)}
                  />
                  <FormHelperText>
                    Toggle the resolved environment-level value.
                  </FormHelperText>
                </>
              ) : (
                <Input
                  type={draftValueType === "int" ? "number" : "text"}
                  value={String(draftValue ?? "")}
                  onChange={(event) => {
                    const nextValue = event.target.value;
                    setDraftValue(
                      draftValueType === "int"
                        ? Number.parseInt(nextValue || "0", 10)
                        : nextValue,
                    );
                  }}
                />
              )}
            </FormControl>

            <FormControl>
              <FormLabel>Description</FormLabel>
              <Input
                value={draftDescription}
                onChange={(event) => setDraftDescription(event.target.value)}
                placeholder="Optional note for other admins"
              />
            </FormControl>

            {modalError && (
              <Alert color="danger" variant="soft">
                {modalError}
              </Alert>
            )}
          </Stack>

          <DialogActions>
            <Button variant="plain" color="neutral" onClick={closeEdit}>
              Cancel
            </Button>
            <Button loading={saving} onClick={handleSave}>
              Save
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>
    </Stack>
  );
};

const MetalMindAdminFeatureFlags: React.FC = () => {
  const listFlags = useListMetalMindAdminFeatureFlags();
  const setFlag = useSetMetalMindAdminFeatureFlag();
  const deleteFlag = useDeleteMetalMindAdminFeatureFlag();
  const [items, setItems] = useState<EnvFeatureFlag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const rows = await listFlags();
      setItems(rows);
    } catch (err: unknown) {
      setError(
        getAdminErrorMessage(
          err,
          "Failed to load MetalMind Admin feature flags.",
        ),
      );
    } finally {
      setLoading(false);
    }
  }, [listFlags]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = useCallback(
    async (flagKey: string, nextValue: EnvFeatureFlagWrite) => {
      await setFlag(flagKey, nextValue);
      await load();
    },
    [load, setFlag],
  );

  const handleDelete = useCallback(
    async (flagKey: string) => {
      await deleteFlag(flagKey);
      await load();
    },
    [deleteFlag, load],
  );

  return (
    <MetalMindAdminFeatureFlagsView
      items={items}
      loading={loading}
      error={error}
      onSave={handleSave}
      onDelete={handleDelete}
    />
  );
};

export default MetalMindAdminFeatureFlags;
